/**
 * Component registry — maps tool_name → React component used to render the
 * tool's result inline inside a chat message.
 *
 * HANDOFF_AI_CHAT_FEATURE_V1_REVISED visual-feedback addendum §4.
 *
 * The keys are the tool names from tools/chat_tools.py::TOOL_SCHEMAS. Tools
 * not listed here render with the default <ToolResultJsonFallback>.
 *
 * V1 is intentionally minimal — only update_deal_notes and mark_deal_status
 * have dedicated rich renderers (notes confirmation banner, status diff
 * pill). Everything else falls through to the JSON dump renderer; richer
 * inline renderers (BenchmarkSummaryRow, etc.) ship as later passes plug new
 * entries into this map.
 */
import type { ComponentType } from 'react';

import { ArchiveDealBanner } from '../components/ArchiveDealBanner';
import { BenchmarkSummaryRow } from '../components/BenchmarkSummaryRow';
import { ChatExportCard } from '../components/ChatExportCard';
import { ChatScoreBreakdownCard } from '../components/ChatScoreBreakdownCard';
import { ChatTermSheetCard } from '../components/ChatTermSheetCard';
import { ComponentDetailCard } from '../components/ComponentDetailCard';
import { CostOfCreditCard } from '../components/CostOfCreditCard';
import { CovenantExplanationCard } from '../components/CovenantExplanationCard';
import { DealFieldUpdatedBanner } from '../components/DealFieldUpdatedBanner';
import { GetComponentDetailCard } from '../components/GetComponentDetailCard';
import { ListDealsCard } from '../components/ListDealsCard';
import { NoteUpdatedBanner } from '../components/NoteUpdatedBanner';
import { PGRiskCard } from '../components/PGRiskCard';
import { RepaymentShapeCard } from '../components/RepaymentShapeCard';
import { StatusBadgeChange } from '../components/StatusBadgeChange';

import type { ToolResultProps } from '../types';

export type { ToolResultProps };

// V1.4: create_deal removed from registry — deal creation now happens
// client-side via ChatDealCreationCard (driven by the deal_creation_prompt
// SSE event, not a tool result). CreateDealConfirmCard is kept on disk as
// dead code in case we revert; remove file in V1.5.
export const componentRegistry: Record<string, ComponentType<ToolResultProps>> = {
  list_deals: ListDealsCard,
  get_latest_term_sheet: ChatTermSheetCard,
  update_deal_notes: NoteUpdatedBanner,
  mark_deal_status: StatusBadgeChange,
  get_benchmarks: BenchmarkSummaryRow,
  get_analysis: ChatScoreBreakdownCard,
  get_full_deal_context: ChatScoreBreakdownCard,
  // Client-injected when a user clicks a component row in ChatScoreBreakdownCard.
  component_detail: ComponentDetailCard,
  // Chat-driven path: get_component_detail (T-37) slices get_analysis to one
  // component; GetComponentDetailCard adapts it onto the same ComponentDetailCard.
  get_component_detail: GetComponentDetailCard,
  // T-39 / T-40 — loan_math-derived repayment shape + cost of credit.
  get_repayment_shape: RepaymentShapeCard,
  get_cost_of_credit: CostOfCreditCard,
  // T-27 — deal-anchored covenant explanation (content + own threshold + percentile).
  get_covenant_explanation: CovenantExplanationCard,
  // T-38 — personal-guarantee risk profile (adverse-only, council-approved copy).
  get_personal_guarantee_risk: PGRiskCard,
  archive_deal: ArchiveDealBanner,
  update_deal_field: DealFieldUpdatedBanner,
  generate_export: ChatExportCard,
};
